<template>
  <div class="pdf-preview-wrap" style="position: relative;">
    <LoadingSpinner v-if="loadingPDF" :display-text="$gettext('Generating preview')" />
    <GekkoButton
      v-if="invoice && invoice.status === 'd' && !loadingPDF && !showMobileViewer && !forceMobileViewer"
      @click="$emit('show-design')"
      :text="$gettext('Edit design')"
      :icon="'fa-solid fa-paintbrush'"
      variant="primary"
      class="edit-design-button"
    />
    <iframe v-if="!forceMobileViewer && !showMobileViewer && !loadingPDF && invoicePDF" id="pdf-frame" class="pdf-preview" :src="invoicePDF + '#toolbar=1'"></iframe>
    <GkPDFViewer v-if="(forceMobileViewer || showMobileViewer) && !loadingPDF && invoicePDF" :item-pdf="invoicePDF" ref="gp-pdf-viewer" toolbar>
      <template v-slot:title>
        <div>
          {{ invoice.number }}
        </div>
      </template>
      <template v-slot:actions>
        <GekkoButton
          @click="downloadInvoice(invoiceId)"
          :icon="'fa-solid fa-download'"
          variant="link"
          size="sm"
          class="px-0 text-white"
        />
        <GekkoButton
          v-if="invoice && invoice.status === 'd'"
          :text="$gettext('Edit design')"
          @click="$emit('show-design')"
          :icon="'fa-solid fa-paintbrush'"
          variant="primary"
          size="sm"
        />
      </template>
    </GkPDFViewer>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'

import invoices from "@/mixins/invoices";
import pdfviewer from "@/mixins/pdfviewer";

import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'

import InvoiceLayoutService from '@/store/services/invoice.layout.service.js'
import InvoiceService from "@/store/services/invoice.service.js"

import GkPDFViewer from '@/components/invoices/ui/GkPDFViewer'

export default {
  name: "InvoicePDFPreview",
  mixins: [invoices, pdfviewer],
  components: {
    GekkoButton,
    LoadingSpinner,
    GkPDFViewer
  },
  props: {
    invoiceId: {
      type: [Number, String],
      default: null
    },
    isQuotation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loadingPDF: true,
      controllerSignal: null
    }
  },
  computed: {
    ...mapGetters({ 
      invoice: "invoices/getInvoice",
      invoicePDF: "invoices/getInvoicePDF",
    }),
    isRecurrent () {
      return this.invoice.status === 'recurrent' // pdf does not exist yet
    }
  },
  created () {
    this.setupData()
  },
  beforeDestroy () {
    this.cancelPDFRequest()
  },
  methods: {
    ...mapMutations({ 
      setInvoicePDF: 'invoices/SET_INVOICE_PDF',
    }),
    setupData () {
      if (this.isQuotation) {
        this.getQuotationPDF()
      } else {
        this.getInvoicePDF()
        // this.invoicePDF = '/static/img/test-pdf-sample.pdf'
        // this.loadingPDF = false
      }
    },
    cancelPDFRequest () {
      if (this.controllerSignal) {
        this.controllerSignal.abort()
      }
    },
    getInvoicePDF () {
      if (!this.invoicePDF) {
        if (this.invoiceId) {
          if (this.isRecurrent) {
            InvoiceLayoutService.previewPdf(this.getInvoicePreviewData()).then((response) => {
              this.setInvoicePDF(response.replace('application/octet-stream', 'application/pdf'))
              this.loadingPDF = false
            })
          } else {
            this.controllerSignal = new AbortController();
            InvoiceLayoutService.getInvoicePDFb64(this.invoiceId, this.controllerSignal).then((response) => {
              this.setInvoicePDF(response.replace('application/octet-stream', 'application/pdf'))
              this.loadingPDF = false
            }).catch((error) => {
              this.loadingPDF = false
            })
          }
        }
      } else {
        this.loadingPDF = false
      }
    },
    getQuotationPDF () {
      if (!this.invoicePDF) {
        if (this.invoiceId) {
          this.controllerSignal = new AbortController();
          InvoiceLayoutService.getQuotationPDFb64(this.invoiceId, this.controllerSignal).then((response) => {
            this.setInvoicePDF(response.replace('application/octet-stream', 'application/pdf'))
            this.loadingPDF = false
          }).catch(() => {
            this.loadingPDF = false
          })
        }
      } else {
        this.loadingPDF = false
      }
    },
    getInvoicePreviewData () {
      // get totals (fn copied from InvoiceLines.vatTotals())
      const vat_totals = {}
      this.invoice.invoice_lines.forEach(invoice_line => {
        if((invoice_line.amount > 0 || invoice_line.amount < 0) && !invoice_line.vat_exempt) {
          if (!vat_totals[invoice_line.tax_rate]) {
            vat_totals[invoice_line.tax_rate] = 0
          }
          if (this.invoice.line_amount_is_gross) {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100 - Math.round((invoice_line.multiplier * invoice_line.amount / ( 1 + invoice_line.tax_rate / 100)) * 100) / 100
          } else {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * (invoice_line.tax_rate / 100) * 100) / 100
          }
        }
      })

      const previewData = {
        invoice: this.invoice, 
        totals: {
          vat_totals: Object.entries(vat_totals).map(([key, value]) => [parseInt(key), value]),
          subtotal: this.invoice.subtotal_amount,
          total: this.invoice.total_amount
        },
        layout: this.invoice.layout, 
        recipient: this.invoice.recipient_customer
      }
      return previewData
    },
    downloadInvoice(invoice_id) {
      InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
        this.promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
  }
}
</script>
<style scoped lang="scss">

.pdf-preview-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  .pdf-preview {
    height: 100%;
    width: 100%;
    border: 0;
  }
}

</style>